import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Typography, TextField, InputAdornment, Avatar, Button, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, RadioGroup, FormControlLabel, Radio,
  Alert, CircularProgress, IconButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import LogoutIcon from "@mui/icons-material/Logout";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useImpersonation } from "../../contexts/ImpersonationContext";

const MOTIVOS = [
  { k: "soporte", l: "Soporte a cliente" },
  { k: "qa", l: "QA / Validación de feature" },
  { k: "pruebas", l: "Pruebas de desarrollo" },
  { k: "onboarding", l: "Onboarding de usuario" },
  { k: "otro", l: "Otro" },
];

export default function Impersonation() {
  const { isActive, actingAsUser, expiresAt, remainingMs, start, stop, listAvailable, refreshMe } =
    useImpersonation();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("");
  const [confirmTarget, setConfirmTarget] = useState(null); // { user, motivo, notas }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      await refreshMe().catch(() => {});
      const list = await listAvailable();
      setUsers(list);
    } catch (e) {
      if (e.status === 403) {
        setError("Solo los superadmins pueden actuar como otros usuarios.");
      } else {
        setError(`No se pudo cargar la lista: ${e.message}`);
      }
    } finally {
      setLoading(false);
    }
  }

  const filtered = useMemo(() => {
    if (!filter) return users;
    const f = filter.toLowerCase();
    return users.filter((u) => {
      const n = `${u.name || ""} ${u.lastname || ""}`.toLowerCase();
      const e = (u.email || "").toLowerCase();
      const r = (u.role || "").toLowerCase();
      return n.includes(f) || e.includes(f) || r.includes(f);
    });
  }, [users, filter]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 980, mx: "auto" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
        <Box
          sx={{
            width: 40, height: 40, borderRadius: 2,
            background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
            display: "grid", placeItems: "center", color: "#fff",
          }}
        >
          <SwapHorizIcon />
        </Box>
        <Box>
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: "#0f172a" }}>
            Actuar como otro usuario
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: "#64748b" }}>
            Solo superadmins. Cada acción queda auditada.
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }} />
        <IconButton onClick={load} disabled={loading} title="Recargar">
          <RefreshIcon />
        </IconButton>
      </Box>

      {isActive && (
        <Box
          sx={{
            p: 2, borderRadius: 2, mb: 2,
            background: "linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)",
            border: "1px solid #fdba74",
            display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap",
          }}
        >
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography sx={{ fontSize: 11, fontWeight: 800, color: "#d97706", letterSpacing: 0.8 }}>
              SESIÓN ACTIVA
            </Typography>
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#92400e" }}>
              Actuando como{" "}
              {actingAsUser
                ? `${actingAsUser.name || ""} ${actingAsUser.lastname || ""}`.trim() || actingAsUser.email
                : "—"}
            </Typography>
            {expiresAt && (
              <Typography sx={{ fontSize: 12, color: "#92400e" }}>
                Expira: {new Date(expiresAt).toLocaleString()} ·{" "}
                {remainingMs != null ? formatRemaining(remainingMs) + " restantes" : ""}
              </Typography>
            )}
          </Box>
          <Button
            variant="contained"
            onClick={stop}
            startIcon={<LogoutIcon />}
            sx={{
              background: "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
              "&:hover": { opacity: 0.92 },
              textTransform: "none", fontWeight: 700,
            }}
          >
            Salir de impersonation
          </Button>
        </Box>
      )}

      <TextField
        fullWidth
        size="small"
        placeholder="Buscar usuario por nombre, email o rol..."
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        InputProps={{
          startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
        }}
        sx={{ mb: 2 }}
      />

      {loading ? (
        <Box sx={{ display: "grid", placeItems: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity={error.startsWith("Solo") ? "info" : "warning"}>{error}</Alert>
      ) : filtered.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 6, color: "#64748b" }}>
          <Typography>No hay usuarios que mostrar.</Typography>
        </Box>
      ) : (
        <Box sx={{ display: "grid", gap: 1.2 }}>
          {filtered.map((u) => (
            <UserRow
              key={u.id}
              user={u}
              disabled={isActive}
              onSelect={() => setConfirmTarget({ user: u, motivo: "pruebas", notas: "" })}
            />
          ))}
        </Box>
      )}

      <ConfirmDialog
        open={!!confirmTarget}
        target={confirmTarget}
        onCancel={() => setConfirmTarget(null)}
        onConfirm={async ({ motivo, notas }) => {
          try {
            await start(confirmTarget.user.id, motivo, 4, notas || null);
            setConfirmTarget(null);
          } catch (e) {
            alert(`No se pudo iniciar: ${e.message || e}`);
          }
        }}
      />
    </Box>
  );
}

function UserRow({ user, disabled, onSelect }) {
  const name = `${user.name || ""} ${user.lastname || ""}`.trim() || user.email;
  return (
    <Box
      sx={{
        display: "flex", alignItems: "center", gap: 1.5,
        p: 1.5, borderRadius: 2, bgcolor: "#fff",
        border: "1px solid #e2e8f0",
        "&:hover": { borderColor: "#cbd5e1", boxShadow: "0 2px 10px rgba(0,0,0,0.04)" },
      }}
    >
      <Avatar sx={{ bgcolor: "#1e3a5f1a", color: "#1e3a5f", fontWeight: 700 }}>
        {(name || "?")[0]?.toUpperCase()}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 14.5, fontWeight: 700, color: "#0f172a" }}>{name}</Typography>
        <Typography sx={{ fontSize: 12, color: "#64748b" }}>
          {user.email}
          {user.role ? ` · ${user.role}` : ""}
        </Typography>
      </Box>
      {user.activo === false && <Chip size="small" label="Inactivo" color="default" />}
      <Button
        variant="contained"
        onClick={onSelect}
        disabled={disabled}
        sx={{
          background: "linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)",
          textTransform: "none", fontWeight: 700,
          "&:hover": { opacity: 0.92 },
        }}
      >
        Actuar como
      </Button>
    </Box>
  );
}

function ConfirmDialog({ open, target, onConfirm, onCancel }) {
  const [motivo, setMotivo] = useState("pruebas");
  const [notas, setNotas] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setMotivo("pruebas");
      setNotas("");
      setBusy(false);
    }
  }, [open]);

  if (!open || !target) return null;
  const name = `${target.user.name || ""} ${target.user.lastname || ""}`.trim() || target.user.email;

  return (
    <Dialog open={open} onClose={busy ? undefined : onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>Confirmar impersonation</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 13, color: "#475569" }}>
          Estás a punto de actuar como <strong>{name}</strong>. Todas tus acciones quedarán
          registradas con tu identidad real. La sesión expira en 4 horas.
        </Typography>
        <Typography sx={{ fontSize: 12.5, color: "#0f172a", mt: 2, fontWeight: 700 }}>
          Motivo
        </Typography>
        <RadioGroup value={motivo} onChange={(e) => setMotivo(e.target.value)}>
          {MOTIVOS.map((m) => (
            <FormControlLabel
              key={m.k}
              value={m.k}
              control={<Radio size="small" />}
              label={<Typography sx={{ fontSize: 13 }}>{m.l}</Typography>}
            />
          ))}
        </RadioGroup>
        <TextField
          fullWidth multiline minRows={2}
          size="small"
          label="Notas (opcional, queda en auditoría)"
          value={notas}
          onChange={(e) => setNotas(e.target.value)}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onCancel} disabled={busy}>Cancelar</Button>
        <Button
          variant="contained"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            await onConfirm({ motivo, notas });
            setBusy(false);
          }}
          sx={{
            background: "linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)",
            textTransform: "none", fontWeight: 700,
          }}
        >
          {busy ? "Iniciando..." : `Confirmar y actuar como ${name.split(" ")[0]}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function formatRemaining(ms) {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  if (h <= 0) return `${m}m`;
  return `${h}h ${m}m`;
}
